import { useRef, useState } from 'react'
import { AxiosError } from 'axios'
import {
  IconArrowLeft,
  IconCheck,
  IconInfoCircle,
  IconPlus,
  IconX,
} from '@tabler/icons-react'
import { IconDownload } from '@tabler/icons-react'
import { useGetFileHistory } from '@/services/fileService'
import {
  useImportQuestionFile,
  useSaveQuesionFile,
} from '@/services/importQuestionService'
import { ImportQuesitonRes } from '@/types/question.type'
import { toast } from 'sonner'
import importImg from '@/assets/svg/upload_file.svg'
import caseTemplateUrl from '@/assets/templateQuestionFile/Question Matrix Template - Case Study Questions.xlsx?url'
// Import bundled template assets as URL to ensure they work in dev and build
import nonCaseTemplateUrl from '@/assets/templateQuestionFile/Question Matrix Template - Non-Case Study Questions.xlsx?url'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip'
import FileImportedDetail from './ImportQuestion/FileImportedDetail'

interface Props {
  moduleId: string
  open: boolean
  onOpenChange: (open: boolean) => void
  refreshChapter: () => void
}

const ImportQuestionModal = ({
  open,
  moduleId,
  onOpenChange,
  refreshChapter,
}: Props) => {
  const [file, setFile] = useState<File | null>(null)
  const [importRes, setImportRes] = useState<ImportQuesitonRes | null>(null)
  const [isDragOver, setIsDragOver] = useState(false)
  const fileInputRef = useRef<HTMLInputElement | null>(null)

  const { importFileAsync, isImportingFile } = useImportQuestionFile()
  const { saveQuestionFileAsync, isSaving } = useSaveQuesionFile()

  const { files } = useGetFileHistory(moduleId)

  const clearSelectedFile = () => {
    setFile(null)
    if (fileInputRef.current) fileInputRef.current.value = ''
  }

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
    setIsDragOver(true)
  }

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
    setIsDragOver(false)
  }

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
    setIsDragOver(false)

    const files = e.dataTransfer.files
    if (files && files.length > 0) {
      const droppedFile = files[0]
      const isCsv =
        droppedFile.type === 'text/csv' ||
        droppedFile.name.toLowerCase().endsWith('.csv')
      const isXlsx =
        droppedFile.type ===
          'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' ||
        droppedFile.type === 'application/vnd.ms-excel' ||
        droppedFile.name.toLowerCase().endsWith('.xlsx')

      if (isCsv || isXlsx) {
        setFile(droppedFile)
      } else {
        toast.error('Please upload only CSV or Excel files')
      }
    }
  }

  // Shared helpers for downloading templates
  const isLikelyExcelBlob = async (blob: Blob) => {
    const header = await blob.slice(0, 2).arrayBuffer()
    const bytes = new Uint8Array(header)
    return bytes.length === 2 && bytes[0] === 0x50 && bytes[1] === 0x4b
  }

  const tryDownloadAsBlob = async (
    url: string | null | undefined,
    preferredFileName: string
  ) => {
    if (!url) return false
    try {
      const res = await fetch(url)
      if (!res.ok) return false
      const contentType = res.headers?.get?.('content-type') ?? ''
      const isExcelContentType =
        /application\/(vnd\.openxmlformats-officedocument\.spreadsheetml\.sheet|octet-stream)/i.test(
          contentType
        )
      const blob = await res.blob()
      const looksLikeExcel =
        isExcelContentType || (await isLikelyExcelBlob(blob))
      if (!looksLikeExcel) return false
      const blobUrl = URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = blobUrl
      link.download = preferredFileName
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      URL.revokeObjectURL(blobUrl)
      return true
    } catch {
      return false
    }
  }

  const downloadFromUrlList = async (
    preferredFileName: string,
    urls: Array<string | undefined>
  ) => {
    for (const url of urls) {
      if (await tryDownloadAsBlob(url, preferredFileName)) return
    }
    // Final CSV fallback if all sources fail
    try {
      const headers = [
        'question_id',
        'moodle_question_id',
        'in_pt',
        'text',
        'indicative_content',
        'rubric',
        'sub_question_text',
        'sub_question_marks',
        'option_text',
        'option_is_correct',
        'marking_scheme_level',
        'mark_range',
        'description',
      ]
      const exampleRow = [
        '',
        '',
        'false',
        'Question text here',
        '',
        '',
        'Sub question text',
        '1',
        'Option text',
        'false',
        '1',
        '0-1',
        'Descriptor',
      ]
      const csv = [headers.join(','), exampleRow.join(',')].join('\n')
      const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' })
      const url = URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = url
      link.setAttribute('download', 'question_template.csv')
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      URL.revokeObjectURL(url)
    } catch {
      toast.error('Failed to download template')
    }
  }

  // Default template download (kept for fallback usages)
  const downloadTemplate = async () => {
    const env = import.meta.env as unknown as Record<string, string | undefined>
    await downloadFromUrlList(
      'Question Matrix Template - Non-Case Study Questions.xlsx',
      [env.VITE_QUESTION_TEMPLATE_NON_CASE_URL, nonCaseTemplateUrl]
    )
  }

  // Specific downloads
  const downloadNonCaseTemplate = async () => {
    const env = import.meta.env as unknown as Record<string, string | undefined>
    await downloadFromUrlList(
      'Question Matrix Template - Non-Case Study Questions.xlsx',
      [env.VITE_QUESTION_TEMPLATE_NON_CASE_URL, nonCaseTemplateUrl]
    )
  }

  const downloadCaseTemplate = async () => {
    const env = import.meta.env as unknown as Record<string, string | undefined>
    await downloadFromUrlList(
      'Question Matrix Template - Case Study Questions.xlsx',
      [env.VITE_QUESTION_TEMPLATE_CASE_URL, caseTemplateUrl]
    )
  }

  const onSubmit = async () => {
    if (!file) return

    const isCsv =
      file.type === 'text/csv' || file.name.toLowerCase().endsWith('.csv')
    const isXlsx =
      file.type ===
        'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' ||
      file.type === 'application/vnd.ms-excel' ||
      file.name.toLowerCase().endsWith('.xlsx')

    if (isCsv || isXlsx) {
      importFileAsync({ moduleId, file })
        .then((data) => {
          setImportRes(data.data)
        })
        .catch((error: AxiosError) => {
          const errorMsg =
            (error.response?.data as { detail?: string })?.detail ||
            'Failed to upload file'
          // clearSelectedFile()
          setImportRes({
            valid_question_ids: 0,
            invalid_question_ids: 0,
            total_questions: 0,
            question_file_id: 0,
            question_ids_invalid: [
              {
                question_id: '',
                invalid_cols: [''],
              },
            ],
            cols_err: errorMsg,
          })
          toast.error(errorMsg, {
            duration: 3000,
            position: 'top-right',
          })
        })
    }
  }

  const handleSave = () => {
    const reqBody = {
      question_file_id: importRes?.question_file_id as number,
      question_ids_invalid: importRes?.question_ids_invalid.map(
        (item) => item.question_id
      ) ?? [''],
      module_id: Number(moduleId ?? '0'),
    }

    saveQuestionFileAsync(reqBody)
      .then(() => {
        toast.success('Save question file successfully', {
          duration: 3000,
          position: 'top-right',
        })
        clearSelectedFile()
        setImportRes(null)
        onOpenChange(false)
        refreshChapter()
      })
      .catch(() => {
        toast.error('Fail to save file', {
          duration: 3000,
          position: 'top-right',
        })
      })
  }

  const downloadHistoryFile = () => {
    try {
      const url = files?.file_url
      if (url) {
        const link = document.createElement('a')
        link.href = url
        link.target = '_blank'
        link.download = files?.file_path
          ? (files?.file_path.split(/[\\/]/).pop() as string)
          : 'file'
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)
        return
      }
      downloadTemplate()
    } catch {
      toast.error('Failed to download file')
    }
  }

  return (
    <Dialog
      open={open}
      onOpenChange={(val) => {
        onOpenChange(val)
        if (!val) {
          clearSelectedFile()
          setImportRes(null)
        }
      }}
    >
      <DialogContent className='gap-2 sm:max-w-[395px]'>
        <DialogHeader className='text-left'>
          <DialogTitle>
            {importRes ? 'Upload Report' : 'Add Question Matrix'}
          </DialogTitle>
        </DialogHeader>
        {importRes && (
          <FileImportedDetail
            resData={importRes}
            fileInputRef={undefined}
            setFile={setFile}
            file={file}
          />
        )}

        {!importRes && (
          <div className='relative'>
            <TooltipProvider delayDuration={0}>
              <Tooltip>
                <TooltipTrigger asChild>
                  <IconInfoCircle className='absolute top-5 left-2 z-50 min-h-6 min-w-6' />
                </TooltipTrigger>
                <TooltipContent className='max-w-[282px]'>
                  <p className=''>
                    To add more questions to this module, please ensure your
                    file follows our template. You can download and use the
                    Non-Case Study and Case Study templates below. Only
                    questions with new IDs can be added.
                  </p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
            <div
              className={`relative mt-3 mb-2 flex min-h-[163px] cursor-pointer flex-col items-center justify-center gap-3 space-y-1 rounded-[12px] border p-6 transition-colors ${
                isDragOver
                  ? 'border-[#5741C6] bg-[#5741C6]/5'
                  : 'border-[#EAECF0]'
              }`}
              onClick={() => fileInputRef.current?.click()}
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onDrop={handleDrop}
            >
              {!file && <img src={importImg} alt='upload img'></img>}
              <Input
                type='file'
                accept='.csv,.xlsx'
                ref={fileInputRef}
                onClick={(e) => {
                  ;(e.currentTarget as HTMLInputElement).value = ''
                }}
                onChange={(e) => {
                  const selected = e.target.files?.[0] ?? null
                  setFile(selected)
                }}
                className='hidden'
              />
              {file ? (
                <div className='w-full max-w-[284px] rounded-md border border-[#EAECF0] px-3 py-2'>
                  <div className='flex items-center justify-between gap-3'>
                    <span
                      className='text-foreground truncate text-sm font-bold'
                      title={file.name}
                    >
                      {file.name}
                    </span>
                    <button
                      type='button'
                      onClick={(e) => {
                        e.stopPropagation()
                        clearSelectedFile()
                      }}
                      className='hover:bg-muted inline-flex h-6 w-6 items-center justify-center rounded'
                      aria-label='Clear file'
                      title='Remove file'
                    >
                      <IconX size={16} />
                    </button>
                  </div>
                </div>
              ) : (
                <div className='text-center text-sm'>
                  {isDragOver ? (
                    <>
                      <b className='text-[#5741C6]'>Drop file here</b> to upload
                    </>
                  ) : (
                    <div>
                      <b className='text-[#5741C6]'>Click to upload</b> or drag
                      and drop Excel or CSV
                      <p className='mt-1 text-xs text-[#475467]'>
                        Make sure to follow the “
                        <a
                          href='https://docs.google.com/document/d/1PO4o-C4p0dQjKCM0YNQCM2Ncy3tV9LmnCQgmIp5jxr8/edit?tab=t.0'
                          target='_blank'
                          rel='noopener noreferrer'
                          onClick={(e) => e.stopPropagation()}
                          className='underline hover:text-[#6ca2e9]'
                        >
                          HOW TO BUILD QUESTION LIBRARY
                        </a>
                        ” guide.
                      </p>
                    </div>
                  )}
                </div>
              )}
            </div>

            <div className='max-w-[345px] rounded-md border border-[#EAECF0] px-3 py-3'>
              {/* file (grid) */}
              <div className='grid grid-cols-[180px_1fr_auto] items-center text-[13px]'>
                <div className='px-1 text-left font-bold'>File</div>
                <div className='text-center font-bold'>Question</div>
                <div className='text-center font-bold'>Download</div>

                {!!files?.file_path && (
                  <>
                    <div className='w-[180px] max-w-[180px] min-w-[180px] truncate px-1 py-2'>
                      {files?.file_path}
                    </div>
                    <div className='py-2 text-center'>
                      {files?.total_questions}
                    </div>
                    <div className='py-2 text-right'>
                      <button
                        type='button'
                        onClick={(e) => {
                          e.stopPropagation()
                          downloadHistoryFile()
                        }}
                        className='hover:bg-muted inline-flex items-center gap-2 rounded-md px-2 py-1 text-sm'
                      >
                        <IconDownload size={24} />
                      </button>
                    </div>
                    <div className='col-span-3 border-b border-[#EAECF0]' />
                  </>
                )}

                <div className='px-1 py-3' style={{ width: 200 }}>
                  Question Matrix Template - Non-Case Study Questions
                </div>
                <div className='py-3 text-center'>N/A</div>
                <div className='py-3 text-right'>
                  <button
                    type='button'
                    onClick={(e) => {
                      e.stopPropagation()
                      downloadNonCaseTemplate()
                    }}
                    className='hover:bg-muted inline-flex items-center gap-2 rounded-md px-2 py-1 text-sm'
                  >
                    <IconDownload size={24} />
                  </button>
                </div>
                <div className='col-span-3 border-b border-[#EAECF0]' />

                <div className='px-1 py-3' style={{ width: 200 }}>
                  Question Matrix Template - Case Study Questions
                </div>
                <div className='py-3 text-center'>N/A</div>
                <div className='py-3 text-right'>
                  <button
                    type='button'
                    onClick={(e) => {
                      e.stopPropagation()
                      downloadCaseTemplate()
                    }}
                    className='hover:bg-muted inline-flex items-center gap-2 rounded-md px-2 py-1 text-sm'
                  >
                    <IconDownload size={24} />
                  </button>
                </div>
                <div className='col-span-3 border-b border-[#EAECF0]' />
              </div>
            </div>
          </div>
        )}
        {!importRes && (
          <DialogFooter className='mt-3 gap-2'>
            <DialogClose asChild>
              <Button
                onClick={() => {
                  clearSelectedFile()
                  setImportRes(null)
                }}
                variant='outline'
              >
                Cancel
              </Button>
            </DialogClose>
            <Button
              className='w-[75px]'
              type='button'
              onClick={onSubmit}
              disabled={!file || isImportingFile}
            >
              {isImportingFile ? (
                <div className='loader' />
              ) : (
                <>
                  <IconPlus />
                  Add
                </>
              )}
            </Button>
          </DialogFooter>
        )}

        {importRes && (
          <DialogFooter className='mt-3 gap-2'>
            <Button
              onClick={() => {
                setImportRes(null)
              }}
              variant='outline'
            >
              <IconArrowLeft />
              Back
            </Button>

            <Button
              className=''
              type='button'
              onClick={() => handleSave()}
              disabled={isSaving || !!importRes?.cols_err}
            >
              {isSaving ? (
                <div className='loader' />
              ) : !!importRes?.cols_err ? (
                <>
                  <IconCheck />
                  Skip Errors & Save
                </>
              ) : (
                <>
                  <IconCheck />
                  Save
                </>
              )}
            </Button>
          </DialogFooter>
        )}
      </DialogContent>
    </Dialog>
  )
}

export default ImportQuestionModal
